import { useState, useEffect } from "react";
import { Cloud, CloudRain, Sun, Wind, Droplets, AlertTriangle, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const weatherIcons = {
  "01d": Sun,
  "01n": Sun,
  "02d": Cloud,
  "02n": Cloud,
  "03d": Cloud,
  "03n": Cloud,
  "04d": Cloud,
  "04n": Cloud,
  "09d": CloudRain,
  "09n": CloudRain,
  "10d": CloudRain,
  "10n": CloudRain,
  "11d": CloudRain,
  "11n": CloudRain,
  "13d": Cloud,
  "13n": Cloud,
  "50d": Cloud,
  "50n": Cloud,
};

export default function WeatherWidget({ compact = false }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWeather = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/weather/choluteca`);
      setWeather(response.data);
      setError(null);
    } catch (err) {
      setError("No se pudo cargar el clima");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeather();
    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white ${compact ? 'h-24' : 'h-48'} flex items-center justify-center`}>
        <RefreshCw className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className={`bg-gradient-to-br from-gray-400 to-gray-500 rounded-xl p-4 text-white ${compact ? 'h-24' : 'h-48'} flex items-center justify-center`}>
        <p className="text-sm opacity-75">{error || "Sin datos"}</p>
      </div>
    );
  }

  const WeatherIcon = weatherIcons[weather.icon] || Cloud;

  if (compact) {
    return (
      <div 
        className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all"
        onClick={fetchWeather}
        data-testid="weather-widget-compact"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <WeatherIcon className="h-10 w-10" />
            <div>
              <p className="text-3xl font-bold">{weather.temperature}°</p>
              <p className="text-xs opacity-75">{weather.description}</p>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="font-medium">Choluteca</p>
            <div className="flex items-center gap-2 text-xs opacity-75">
              <Droplets className="h-3 w-3" />
              {weather.humidity}%
              <Wind className="h-3 w-3 ml-1" />
              {weather.wind_speed} km/h
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg"
      data-testid="weather-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg">Clima en Choluteca</h3>
          <p className="text-xs opacity-75">Honduras</p>
        </div>
        <button 
          onClick={fetchWeather}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          title="Actualizar"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Current Weather */}
      <div className="flex items-center gap-4 mb-4">
        <WeatherIcon className="h-16 w-16" />
        <div>
          <p className="text-5xl font-bold">{weather.temperature}°C</p>
          <p className="text-sm opacity-90 capitalize">{weather.description}</p>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
        <div className="bg-white/10 rounded-lg p-2 text-center">
          <p className="opacity-75 text-xs">Sensación</p>
          <p className="font-semibold">{weather.feels_like}°C</p>
        </div>
        <div className="bg-white/10 rounded-lg p-2 text-center">
          <div className="flex items-center justify-center gap-1">
            <Droplets className="h-3 w-3 opacity-75" />
            <span className="opacity-75 text-xs">Humedad</span>
          </div>
          <p className="font-semibold">{weather.humidity}%</p>
        </div>
        <div className="bg-white/10 rounded-lg p-2 text-center">
          <div className="flex items-center justify-center gap-1">
            <Wind className="h-3 w-3 opacity-75" />
            <span className="opacity-75 text-xs">Viento</span>
          </div>
          <p className="font-semibold">{weather.wind_speed} km/h</p>
        </div>
      </div>

      {/* Rain Probability */}
      {weather.rain_probability > 0 && (
        <div className="flex items-center gap-2 bg-white/10 rounded-lg p-3 mb-4">
          <CloudRain className="h-5 w-5" />
          <div>
            <p className="text-sm font-medium">Probabilidad de lluvia</p>
            <p className="text-xs opacity-75">{weather.rain_probability}%</p>
          </div>
        </div>
      )}

      {/* Forecast */}
      {weather.forecast && weather.forecast.length > 0 && (
        <div className="border-t border-white/20 pt-4">
          <p className="text-xs opacity-75 mb-2">Pronóstico</p>
          <div className="grid grid-cols-3 gap-2">
            {weather.forecast.map((day, index) => {
              const DayIcon = weatherIcons[day.icon] || Cloud;
              return (
                <div key={index} className="bg-white/10 rounded-lg p-2 text-center">
                  <p className="text-xs opacity-75">{day.day}</p>
                  <DayIcon className="h-6 w-6 mx-auto my-1" />
                  <p className="text-sm font-semibold">{day.temp_max}°</p>
                  <p className="text-xs opacity-75">{day.temp_min}°</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Alerts */}
      {weather.alerts && weather.alerts.length > 0 && (
        <div className="mt-4 bg-amber-500/20 rounded-lg p-3">
          <div className="flex items-center gap-2 text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">Alerta meteorológica</span>
          </div>
          {weather.alerts.map((alert, index) => (
            <p key={index} className="text-xs mt-1 opacity-90">{alert}</p>
          ))}
        </div>
      )}

      {/* Mock data indicator */}
      {weather.is_mock && (
        <p className="text-xs opacity-50 mt-3 text-center">
          Datos de ejemplo
        </p>
      )}
    </div>
  );
}
